import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, map, Observable, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { BreadcrumbService, EnumObjectExtended, EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import ObjectId from 'bson-objectid';
import { UNIT_OF_MEASURE_LABELS, UnitOfMeasureType } from '@app/models/item/enum/UnitOfMeasureType';
import { ProductRecipe } from '@app/models/item/product-recipe';
import { ClientPurchaseOrder } from '@app/models/order/client-purchase-order';
import { Batch, BatchStatus } from '@app/models/production/batch/batch';
import {
  ProductionWorkOrder,
  ProductionWorkOrderInsert,
  ProductionWorkOrderItemInsert,
  ProductionWorkOrderReservationInsert,
  ProductionWorkOrderStatus,
} from '@app/models/production/production-work-order';
import {
  ProductionWorkOrderForm,
  ProductionWorkOrderItemForm,
  ProductionWorkOrderReservationForm,
} from '@app/models/production/production-work-order-forms';
import { ProductionBatchRoles, ProductionWorkOrderRoles } from '@app/models/roles/roles-group';
import { ProductRecipeProxyService } from '@app/proxies/item/product-recipe.proxy.service';
import { ClientPurchaseOrderProxyService } from '@app/proxies/order/client-purchase-order.proxy.service';
import { BatchProxyService } from '@app/proxies/production/batch.proxy.service';
import { ProductionWorkOrderProxyService } from '@app/proxies/production/production-work-order.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

@Component({
  selector: 'app-production-work-order-details',
  templateUrl: './production-work-order-details.component.html',
  styleUrl: './production-work-order-details.component.scss',
})
export class ProductionWorkOrderDetailsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly enumService = inject(EnumService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly pwoProxyService = inject(ProductionWorkOrderProxyService);
  private readonly recipeProxyService = inject(ProductRecipeProxyService);
  private readonly clientPOProxyService = inject(ClientPurchaseOrderProxyService);
  private readonly batchProxyService = inject(BatchProxyService);

  private readonly destroy$ = new Subject<void>();

  isLoading = signal(0);
  isNew = signal(true);
  isSaving = signal(false);
  currentId = signal<string | null>(null);
  headerTitle = signal('New Production Work Order');

  productionWorkOrder = signal<ProductionWorkOrder | null>(null);

  selectedRecipe = signal<ProductRecipe | null>(null);

  relatedBatches = signal<Batch[]>([]);

  canCreateBatch = computed(() => {
    return !this.isNew() && AuthService.hasRole(ProductionBatchRoles.ProductionBatchEditor);
  });

  statusEnumObject?: EnumObjectExtended = this.enumService.getEnum('ProductionWorkOrderStatus', 'productionEnums');
  private readonly batchStatusOptions: EnumValues = this.enumService.getEnum('BatchStatus', 'productionEnums')?.values ?? {};
  statusOptions: EnumValues = this.statusEnumObject?.values ?? {};

  status = new FormControl<ProductionWorkOrderStatus | null>(null);

  headerForm = this.fb.group<ProductionWorkOrderForm>({
    estimatedStartDate: this.fb.control<Date | null>(null),
    estimatedEndDate: this.fb.control<Date | null>(null),
  });

  itemsFormArray = this.fb.array<FormGroup<ProductionWorkOrderItemForm>>([]);

  statusLabel = computed(() => {
    const pwo = this.productionWorkOrder();
    if (!pwo) return '';
    return this.statusOptions[pwo.status] ?? `Status ${pwo.status}`;
  });

  canEdit = computed(() => {
    if (!AuthService.hasRole(ProductionWorkOrderRoles.ProductionWorkOrderEditor)) return false;
    if (this.isNew()) return true;
    const pwo = this.productionWorkOrder();
    return pwo?.status === ProductionWorkOrderStatus.Draft;
  });

  private readonly statusTransitionMap: Record<number, ProductionWorkOrderStatus[]> = {
    [ProductionWorkOrderStatus.Draft]: [ProductionWorkOrderStatus.Draft, ProductionWorkOrderStatus.Approved],
    [ProductionWorkOrderStatus.Approved]: [ProductionWorkOrderStatus.Approved, ProductionWorkOrderStatus.InProduction],
    [ProductionWorkOrderStatus.InProduction]: [ProductionWorkOrderStatus.InProduction, ProductionWorkOrderStatus.Completed],
  };

  availableStatuses = computed<EnumValues>(() => {
    const pwo = this.productionWorkOrder();
    if (!pwo) {
      return Object.fromEntries(Object.entries(this.statusOptions).filter(([key]) => Number(key) === ProductionWorkOrderStatus.Draft));
    }
    const allowed = this.statusTransitionMap[pwo.status] ?? [pwo.status];
    return Object.fromEntries(
      Object.entries(this.statusOptions).filter(([key]) => allowed.includes(Number(key) as ProductionWorkOrderStatus)),
    );
  });

  canChangeStatus = computed(() => {
    if (this.isNew()) return false;
    if (!AuthService.hasRole(ProductionWorkOrderRoles.ProductionWorkOrderEditor)) return false;
    return Object.keys(this.availableStatuses()).length > 1;
  });

  statusSeverity = computed<'success' | 'info' | 'warn' | 'danger' | 'secondary'>(() => {
    const pwo = this.productionWorkOrder();
    if (!pwo) return 'secondary';
    switch (pwo.status) {
      case ProductionWorkOrderStatus.Approved:
      case ProductionWorkOrderStatus.Completed:
        return 'success';
      case ProductionWorkOrderStatus.InProduction:
        return 'info';
      case ProductionWorkOrderStatus.Draft:
        return 'warn';
      case ProductionWorkOrderStatus.Cancelled:
      case ProductionWorkOrderStatus.Deleted:
        return 'danger';
      default:
        return 'secondary';
    }
  });

  private breadcrumbService = inject(BreadcrumbService);

  ngOnInit(): void {
    this.setBreadcrumb('Breadcrumb_New_Production_Work_Order');
    this.setupStatusListener();
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadProductionWorkOrder(id);
    } else {
      this.isNew.set(true);
      this.status.setValue(ProductionWorkOrderStatus.Draft, { emitEvent: false });
    }
  }

  private setupStatusListener(): void {
    this.status.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newStatus) => {
      const current = this.productionWorkOrder()?.status;
      if (newStatus === null || newStatus === undefined) return;
      if (current === undefined) return;
      if (newStatus === current) return;
      this.confirmChangeStatus(newStatus);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private statusLabelFor(status: ProductionWorkOrderStatus): string {
    return this.statusOptions[status] ?? ProductionWorkOrderStatus[status] ?? `Status ${status}`;
  }

  private refreshHeader(pwo: ProductionWorkOrder | null): void {
    if (pwo) {
      this.headerTitle.set(
        this.localeHelperService.translateWithFallback(
          'productionWorkOrder.titleWithNumber',
          `Production Work Order : ${pwo.number ?? ''}`,
          {
            number: pwo.number ?? '',
          },
        ),
      );
      this.setBreadcrumb(pwo.number ?? '');
    } else {
      this.headerTitle.set(this.localeHelperService.translateWithFallback('productionWorkOrder.new', 'New Production Work Order'));
      this.setBreadcrumb('Breadcrumb_New_Production_Work_Order');
    }
  }

  private setBreadcrumb(current: string): void {
    this.breadcrumbService.set([
      { label: 'Breadcrumb_Production' },
      { label: 'Breadcrumb_Production_Work_Orders', route: '/production-work-orders' },
      { label: current },
    ]);
  }

  loadProductionWorkOrder(id: string): void {
    this.incrementLoading();
    this.pwoProxyService
      .getById(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.decrementLoading()),
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.productionWorkOrder.set(response.data);
            this.status.setValue(response.data.status, { emitEvent: false });
            this.refreshHeader(response.data);
            this.populateForm(response.data);
            this.loadRelatedBatches(response.data.id);
          }
        },
      });
  }

  private populateForm(pwo: ProductionWorkOrder): void {
    this.headerForm.patchValue({
      estimatedStartDate: pwo.estimatedStartDate ? new Date(pwo.estimatedStartDate) : null,
      estimatedEndDate: pwo.estimatedEndDate ? new Date(pwo.estimatedEndDate) : null,
    });

    this.itemsFormArray.clear();
    pwo.items.forEach((item) => {
      const recipeName = item.productRecipe?.name ?? item.productRecipeNumber;
      const itemGroup = this.createItemFormGroup(item.productRecipeNumber, recipeName, item.id);
      itemGroup.patchValue({ quantity: item.quantity });

      const reservationsArray = itemGroup.controls.reservations;
      item.reservations.forEach((res) => {
        const resGroup = this.createReservationFormGroup();
        resGroup.patchValue({
          clientPurchaseOrderReference: res.clientPurchaseOrderReference,
          clientPurchaseOrderNumber: res.clientPurchaseOrderNumber,
          clientName: res.client?.name ?? res.client?.code ?? null,
          clientPurchaseOrderItemId: res.clientPurchaseOrderItemId,
          clientPurchaseOrderProductRecipeNumber: res.clientPurchaseOrderProductRecipeNumber,
          reservedQuantity: res.reservedQuantity,
        });
        reservationsArray.push(resGroup);
      });

      this.itemsFormArray.push(itemGroup);
    });
  }

  public fetchRecipes = (query: string): Observable<ProductRecipe[]> =>
    this.recipeProxyService
      .search({ number: query, name: query }, 1, 10, undefined, -1, true)
      .pipe(map((response) => response.data?.items ?? []));

  onRecipeSelected(event: { value: ProductRecipe }): void {
    const recipe = event.value;

    const alreadyExists = this.itemsFormArray.controls.some((group) => group.controls.productRecipeNumber.value === recipe.number);

    if (alreadyExists) {
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('productionWorkOrder.duplicate', 'Duplicate'),
        this.localeHelperService.translateWithFallback(
          'productionWorkOrder.recipeAlreadyAdded',
          `Recipe "${recipe.number}" is already in the items list`,
          {
            recipe: recipe.number,
          },
        ),
      );
      this.selectedRecipe.set(null);
      return;
    }

    const newItemId = this.isNew() ? null : new ObjectId().toHexString();
    const itemGroup = this.createItemFormGroup(recipe.number, recipe.name, newItemId);
    this.itemsFormArray.push(itemGroup);
    this.selectedRecipe.set(null);

    GlobalMessageUtil.success(
      this.localeHelperService.translateWithFallback('productionWorkOrder.itemAdded', 'Item Added'),
      `${recipe.number} — ${recipe.name}`,
    );
  }

  createItemFormGroup(recipeNumber: string, recipeName: string, itemId: string | null = null): FormGroup<ProductionWorkOrderItemForm> {
    return this.fb.group<ProductionWorkOrderItemForm>({
      id: this.fb.control<string | null>(itemId),
      productRecipeNumber: this.fb.control<string | null>({ value: recipeNumber, disabled: true }, [Validators.required]),
      recipeName: this.fb.control<string | null>({ value: recipeName, disabled: true }),
      uom: this.fb.control<number | null>({ value: UnitOfMeasureType.Kg, disabled: true }, [Validators.required]),
      quantity: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
      reservations: this.fb.array<FormGroup<ProductionWorkOrderReservationForm>>([]),
    });
  }

  removeItem(index: number): void {
    this.itemsFormArray.removeAt(index);
  }

  createReservationFormGroup(): FormGroup<ProductionWorkOrderReservationForm> {
    return this.fb.group<ProductionWorkOrderReservationForm>({
      clientPurchaseOrderReference: this.fb.control<string | null>(null, [Validators.required]),
      clientPurchaseOrderNumber: this.fb.control<string | null>(null, [Validators.required]),
      clientName: this.fb.control<string | null>(null),
      clientPurchaseOrderItemId: this.fb.control<string | null>(null, [Validators.required]),
      clientPurchaseOrderProductRecipeNumber: this.fb.control<string | null>(null, [Validators.required]),
      reservedQuantity: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
      mismatchWarning: new FormControl<boolean>(false, { nonNullable: true }),
    });
  }

  getReservations(itemIndex: number): FormArray<FormGroup<ProductionWorkOrderReservationForm>> {
    return this.itemsFormArray.at(itemIndex).controls.reservations;
  }

  removeReservation(itemIndex: number, reservationIndex: number): void {
    this.getReservations(itemIndex).removeAt(reservationIndex);
  }

  /** One picker per line, so the fetch is curried by the line index. */
  public fetchClientPOs =
    (itemIndex: number) =>
    (query: string): Observable<ClientPurchaseOrder[]> => {
      // Exclude CPOs already reserved on this line by id so they never appear in the dropdown.
      const reservedIds = new Set(
        this.getReservations(itemIndex)
          .controls.map((r) => r.controls.clientPurchaseOrderReference.value)
          .filter((id): id is string => !!id),
      );

      return this.clientPOProxyService
        .search({ number: query }, 1, 10)
        .pipe(map((response) => (response.data?.items ?? []).filter((po) => !reservedIds.has(po.id))));
    };

  // Mirrors the recipe pattern: picking a CPO from the search field creates and pushes
  // a new reservation row populated from the selected ClientPurchaseOrder.
  onClientPOSelect(event: { value: ClientPurchaseOrder }, itemIndex: number): void {
    const po = event.value;
    const itemGroup = this.itemsFormArray.at(itemIndex);
    const lineRecipeNumber = itemGroup.getRawValue().productRecipeNumber;

    const reservations = this.getReservations(itemIndex);
    const alreadyAdded = reservations.controls.some((r) => r.controls.clientPurchaseOrderReference.value === po.id);
    if (alreadyAdded) {
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('productionWorkOrder.duplicate', 'Duplicate'),
        this.localeHelperService.translateWithFallback(
          'productionWorkOrder.clientPoAlreadyReserved',
          `Client PO ${po.number} is already reserved for this item`,
          {
            number: po.number,
          },
        ),
      );
      return;
    }

    const resGroup = this.createReservationFormGroup();
    const clientName = po.client?.name ?? po.client?.code ?? null;
    const matchingItem = po.items.find((poItem) => poItem.productRecipeNumber === lineRecipeNumber);

    if (matchingItem) {
      resGroup.patchValue({
        clientPurchaseOrderReference: po.id,
        clientPurchaseOrderNumber: po.number,
        clientName,
        clientPurchaseOrderItemId: matchingItem.id,
        clientPurchaseOrderProductRecipeNumber: matchingItem.productRecipeNumber,
        mismatchWarning: false,
      });
    } else {
      resGroup.patchValue({
        clientPurchaseOrderReference: po.id,
        clientPurchaseOrderNumber: po.number,
        clientName,
        clientPurchaseOrderItemId: null,
        clientPurchaseOrderProductRecipeNumber: null,
        mismatchWarning: true,
      });
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('productionWorkOrder.recipeMismatch', 'Recipe Mismatch'),
        this.localeHelperService.translateWithFallback(
          'productionWorkOrder.clientPoNoMatchingItems',
          `Client PO ${po.number} has no items matching recipe "${lineRecipeNumber}"`,
          { number: po.number, recipe: lineRecipeNumber },
        ),
      );
    }

    reservations.push(resGroup);
  }

  hasReservationMismatch(itemIndex: number, reservationIndex: number): boolean {
    return this.getReservations(itemIndex).at(reservationIndex).controls.mismatchWarning.value;
  }

  save(): void {
    if (this.headerForm.invalid || this.itemsFormArray.length === 0) {
      this.headerForm.markAllAsTouched();
      this.itemsFormArray.controls.forEach((g) => g.markAllAsTouched());
      if (this.itemsFormArray.length === 0) {
        GlobalMessageUtil.warn(
          this.localeHelperService.translateWithFallback('productionWorkOrder.validation', 'Validation'),
          this.localeHelperService.translateWithFallback('productionWorkOrder.addAtLeastOneItem', 'Add at least one item'),
        );
      }
      return;
    }

    const hasInvalidQuantities = this.itemsFormArray.controls.some((g) => !g.controls.quantity.value || g.controls.quantity.value <= 0);
    if (hasInvalidQuantities) {
      this.itemsFormArray.controls.forEach((g) => g.markAllAsTouched());
      return;
    }

    for (let i = 0; i < this.itemsFormArray.length; i++) {
      const itemGroup = this.itemsFormArray.at(i);
      const itemQty = itemGroup.controls.quantity.value ?? 0;
      const reservations = itemGroup.controls.reservations;
      const totalReserved = reservations.controls.reduce((sum, res) => sum + (res.controls.reservedQuantity.value ?? 0), 0);

      if (totalReserved > itemQty) {
        const recipeNumber = itemGroup.getRawValue().productRecipeNumber;
        GlobalMessageUtil.warn(
          this.localeHelperService.translateWithFallback('productionWorkOrder.reservationWarning', 'Reservation Warning'),
          this.localeHelperService.translateWithFallback(
            'productionWorkOrder.reservedExceedsQuantity',
            `Recipe "${recipeNumber}": total reserved quantity (${totalReserved}) exceeds item quantity (${itemQty})`,
            { recipe: recipeNumber, reserved: totalReserved, quantity: itemQty },
          ),
        );
      }
    }

    this.isSaving.set(true);
    this.incrementLoading();

    const headerValues = this.headerForm.getRawValue();
    const startDate = headerValues.estimatedStartDate;
    const endDate = headerValues.estimatedEndDate;

    const items: ProductionWorkOrderItemInsert[] = this.itemsFormArray.getRawValue().map((item) => ({
      productRecipeNumber: item.productRecipeNumber!,
      uom: item.uom!,
      quantity: item.quantity!,
      reservations: (item.reservations || [])
        .filter((res) => res.clientPurchaseOrderReference && res.clientPurchaseOrderItemId)
        .map(
          (res): ProductionWorkOrderReservationInsert => ({
            clientPurchaseOrderReference: res.clientPurchaseOrderReference!,
            clientPurchaseOrderNumber: res.clientPurchaseOrderNumber!,
            clientPurchaseOrderItemId: res.clientPurchaseOrderItemId!,
            clientPurchaseOrderProductRecipeNumber: res.clientPurchaseOrderProductRecipeNumber!,
            reservedQuantity: res.reservedQuantity!,
          }),
        ),
    }));

    if (this.isNew()) {
      const command: ProductionWorkOrderInsert = {
        status: ProductionWorkOrderStatus.Draft,
        items,
        estimatedStartDate: startDate ? startDate.toISOString() : undefined,
        estimatedEndDate: endDate ? endDate.toISOString() : undefined,
      };

      this.pwoProxyService
        .create(command)
        .pipe(
          finalize(() => {
            this.isSaving.set(false);
            this.decrementLoading();
          }),
        )
        .subscribe({
          next: (response) => {
            if (response.success && response.data) {
              GlobalMessageUtil.success(
                this.localeHelperService.translateWithFallback('Common_Success', 'Success'),
                this.localeHelperService.translateWithFallback('PWO_Msg_Create_Success', 'Production Work Order created'),
              );
              this.router.navigate(['/production-work-order-details'], { queryParams: { id: response.data.id } });
            }
          },
        });
    } else {
      const pwo = this.productionWorkOrder()!;
      const rawItems = this.itemsFormArray.getRawValue();
      const updated: ProductionWorkOrder = {
        ...pwo,
        items: items.map((item, idx) => ({
          id: rawItems[idx].id ?? '',
          ...item,
        })),
        estimatedStartDate: startDate ? startDate.toISOString() : undefined,
        estimatedEndDate: endDate ? endDate.toISOString() : undefined,
      };

      this.pwoProxyService
        .update(pwo.id, updated)
        .pipe(
          finalize(() => {
            this.isSaving.set(false);
            this.decrementLoading();
          }),
        )
        .subscribe({
          next: () => {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Common_Success', 'Success'),
              this.localeHelperService.translateWithFallback('PWO_Msg_Update_Success', 'Production Work Order updated'),
            );
            this.loadProductionWorkOrder(pwo.id);
          },
        });
    }
  }

  private confirmChangeStatus(target: ProductionWorkOrderStatus): void {
    const pwo = this.productionWorkOrder();
    if (!pwo) return;

    const previousStatus = pwo.status;
    const targetLabel = this.statusLabelFor(target);
    const currentLabel = this.statusLabelFor(previousStatus);

    this.confirmationService.confirm({
      key: 'general-dialog',
      header: this.localeHelperService.translateWithFallback('PWO_Status_Confirm_Header', 'Change status'),
      message: this.localeHelperService.translateWithFallback(
        'PWO_Status_Confirm_Message',
        `Change the status from ${currentLabel} to ${targetLabel}?`,
        { from: currentLabel, to: targetLabel },
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.localeHelperService.translateWithFallback('Yes', 'Yes'),
      rejectLabel: this.localeHelperService.translateWithFallback('No', 'No'),
      accept: () => {
        this.incrementLoading();
        this.pwoProxyService
          .changeStatus(pwo.id, target)
          .pipe(finalize(() => this.decrementLoading()))
          .subscribe({
            next: (response) => {
              if (response.success && response.data) {
                GlobalMessageUtil.success(
                  this.localeHelperService.translateWithFallback('Common_Success', 'Success'),
                  this.localeHelperService.translateWithFallback('PWO_Msg_Status_Updated', `Status changed to ${targetLabel}`, {
                    status: targetLabel,
                  }),
                );
                this.loadProductionWorkOrder(pwo.id);
              } else {
                this.status.setValue(previousStatus, { emitEvent: false });
              }
            },
            error: () => {
              this.status.setValue(previousStatus, { emitEvent: false });
            },
          });
      },
      reject: () => {
        this.status.setValue(previousStatus, { emitEvent: false });
      },
    });
  }

  loadRelatedBatches(pwoId: string): void {
    this.batchProxyService.getByInitiatorReference(pwoId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.relatedBatches.set(response.data);
        }
      },
      error: () => {
        this.relatedBatches.set([]);
      },
    });
  }

  navigateToCreateBatch(): void {
    const pwo = this.productionWorkOrder();
    if (!pwo) return;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/batch-details'], { queryParams: { id: 'new', pwoId: pwo.id } }));
    window.open(url, '_blank');
  }

  navigateToBatch(batchId: string): void {
    this.router.navigate(['/batch-details'], { queryParams: { id: batchId } });
  }

  back(): void {
    this.router.navigate(['/production-work-orders']);
  }

  getUomLabel(uom: number | null): string {
    if (uom === null) return '';
    return UNIT_OF_MEASURE_LABELS[uom as UnitOfMeasureType] ?? `uom:${uom}`;
  }

  getBatchStatusLabel(status: BatchStatus): string {
    return this.batchStatusOptions[status] ?? `Status ${status}`;
  }

  getBatchStatusSeverity(status: BatchStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case BatchStatus.Approved:
      case BatchStatus.Completed:
      case BatchStatus.Closed:
        return 'success';
      case BatchStatus.ReadyForProduction:
      case BatchStatus.InProduction:
        return 'info';
      case BatchStatus.Draft:
        return 'warn';
      case BatchStatus.Cancelled:
      case BatchStatus.Deleted:
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
